import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { UUID } from "@/Shared/types";
import type { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import { useState } from "react";
import { removeUnavailableAccessorialsForLocation } from "../Helpers/recommendedAccessorialsForLocation";
import { LocationsWizardBody } from "../CustomComponents/Internal/locationsWizardBody";
import LocationsModifyLocationDialogAddressPage from "../Pages/LocationsModifyLocationDialogAddressPage";
import LocationsModifyLocationDialogDetailsPage from "../Pages/LocationsModifyLocationDialogDetailsPage";
import LocationsModifyLocationDialogOtherPage from "../Pages/LocationsModifyLocationDialogOtherPage";

export interface LocationsModifyResult {
  location: Location;
  savedLocationId: UUID;
  locationHasDock?: LoadingCapabilitiesAvailability | undefined;
  locationHasForklift?: LoadingCapabilitiesAvailability | undefined;
}

interface LocationsModifyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: LocationContext;
  locationToModify: SavedLocation;
  onFinished: (result: LocationsModifyResult) => void;
}

const modifyLocationPageDescription = {
  details: "Please enter the details of this location",
  address: "Please enter the remaining parts of the address",
  other: "Please enter other details",
};

// Three-page modify-location wizard (details -> address -> other), hosted in
// a ui-core Dialog. The pages render their own navigation buttons, so the
// dialog supplies no footer. Remount with a fresh `key` per open to reset
// the wizard state.
export function LocationsModifyDialog(props: LocationsModifyDialogProps) {
  // Cleaned on the way in: a location saved before inside was withdrawn from
  // residential would otherwise open with that box ticked and greyed out, with
  // no way for the customer to take it off. Saving persists the cleaned value.
  const [location, setLocation] = useState<SavedLocation>(() => ({
    ...props.locationToModify,
    location: {
      ...props.locationToModify.location,
      accessorials: removeUnavailableAccessorialsForLocation(
        props.locationToModify.location.locationType,
        props.locationToModify.location.accessorials,
      ),
    },
  }));

  const [currentPage, setCurrentPage] = useState<
    "details" | "address" | "other"
  >("details");

  function onDone() {
    props.onFinished({
      location: location.location,
      savedLocationId: location.savedLocationId,
      locationHasDock: location.locationHasDock,
      locationHasForklift: location.locationHasForklift,
    });
    props.onOpenChange(false);
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Modify Location"
      // Each page's intro line belongs to the dialog header, under the title
      // it qualifies — inside the body it read as a caption for whichever
      // field happened to come first.
      description={modifyLocationPageDescription[currentPage]}
      width="wide"
    >
      <ControlPresentationProvider presentation="underlined">
        {/* Consistent page width (matches the add wizard): both keeps the
         * dialog from jumping between pages and stops the address grid from
         * shrink-wrapping. */}
        <LocationsWizardBody testId="modify-location-modal" heightFloor>
          {currentPage === "details" && (
            <LocationsModifyLocationDialogDetailsPage
              onSwitchToAddressPage={() => setCurrentPage("address")}
              setLocation={function (updatedLocation) {
                setLocation({
                  ...location,
                  location: updatedLocation,
                });
              }}
              location={location.location}
              onCancel={() => props.onOpenChange(false)}
              context={props.context}
            />
          )}

          {currentPage === "address" && (
            <LocationsModifyLocationDialogAddressPage
              setLocation={function (updatedLocation) {
                setLocation({
                  ...location,
                  location: updatedLocation,
                });
              }}
              location={location.location}
              onPrevious={() => setCurrentPage("details")}
              onDone={() => setCurrentPage("other")}
            />
          )}

          {currentPage === "other" && (
            <LocationsModifyLocationDialogOtherPage
              setSavedLocation={setLocation}
              savedLocation={location}
              onPrevious={() => setCurrentPage("address")}
              onDone={onDone}
            />
          )}
        </LocationsWizardBody>
      </ControlPresentationProvider>
    </Dialog>
  );
}
